import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DomainAutocomplete from '../../../shared/domain-autocomplete';
import UsersAutocomplete from '../../../shared/users-autocomplete';
import GroupsAutocomplete from '../../../shared/groups-autocomplete';
import { calendarSharingFields } from '../../../shared/shared-profile-field-filter';
import type { RulesEngineFormValues } from './rules-engine-form-page';
import {
  CALENDAR_FIELD_INFO_TYPES,
  OPERATORS,
} from './condition-block-constants';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

interface PersonalCalendarConditionBlockProps {
  blockIndex: number;
  control: Control<RulesEngineFormValues>;
  setValue: UseFormSetValue<RulesEngineFormValues>;
  conditions: any[];
  conditionBlocks: any[];
  domains: string[];
}

export default function PersonalCalendarConditionBlock({
  blockIndex,
  control,
  setValue,
  conditions,
  conditionBlocks,
  domains,
}: PersonalCalendarConditionBlockProps) {
  const handleLogicalOperatorChange = (
    conditionIndex: number,
    operator: 'AND' | 'OR'
  ) => {
    const updatedConditions = [...conditions];
    updatedConditions[conditionIndex - 1] = {
      ...updatedConditions[conditionIndex - 1],
      logicalOperator: operator,
    };
    const updatedBlock = {
      ...conditionBlocks[blockIndex],
      conditions: updatedConditions,
    };
    const updatedBlocks = [...conditionBlocks];
    updatedBlocks[blockIndex] = updatedBlock;
    setValue('conditionBlocks', updatedBlocks);
  };

  return (
    <Stack gap={2}>
      {conditions.map((condition, conditionIndex) => {
        const showValueInput =
          condition.fieldName !== 'sharedToUserInternalToDomain' &&
          condition.fieldName !== 'sharedToUserExternalToDomain';

        return (
          <Box key={condition.id}>
            {conditionIndex > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  mb: 3,
                  alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button
                    variant={
                      conditions[conditionIndex - 1]?.logicalOperator !== 'OR'
                        ? 'contained'
                        : 'outlined'
                    }
                    size="small"
                    sx={{ minWidth: '50px', textTransform: 'none' }}
                    onClick={() =>
                      handleLogicalOperatorChange(conditionIndex, 'AND')
                    }
                  >
                    And
                  </Button>
                  <Button
                    variant={
                      conditions[conditionIndex - 1]?.logicalOperator === 'OR'
                        ? 'contained'
                        : 'outlined'
                    }
                    size="small"
                    sx={{ minWidth: '50px', textTransform: 'none' }}
                    onClick={() =>
                      handleLogicalOperatorChange(conditionIndex, 'OR')
                    }
                  >
                    Or
                  </Button>
                </Box>
              </Box>
            )}
            <Grid container spacing={1} alignItems="flex-start">
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Controller
                  name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.fieldInfoType`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      size="small"
                      label="What kind of information?"
                      value={field.value || ''}
                      onChange={(e) => {
                        const selectedInfoType = e.target.value;
                        field.onChange(selectedInfoType);
                        setValue(
                          `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.fieldName`,
                          ''
                        );
                        setValue(
                          `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.value`,
                          ''
                        );
                      }}
                    >
                      {CALENDAR_FIELD_INFO_TYPES.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <Controller
                  name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.operator`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      size="small"
                      label="Operator"
                      value={field.value || 'equals'}
                    >
                      {OPERATORS.filter((op) => {
                        if (
                          condition.fieldName ===
                            'sharedToUserInternalToDomain' ||
                          condition.fieldName === 'sharedToUserExternalToDomain'
                        ) {
                          return (
                            op.value === 'equals' || op.value === 'notEquals'
                          );
                        }
                        return true;
                      }).map((op) => (
                        <MenuItem key={op.value} value={op.value}>
                          {op.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid
                container
                size={{ xs: 12, sm: 6, md: showValueInput ? 3 : 6 }}
              >
                <Grid size={{ xs: 12, md: 12 }}>
                  <Controller
                    name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.fieldName`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        size="small"
                        label="Which field?"
                        value={field.value || ''}
                        onChange={(e) => {
                          const selectedFieldName = e.target.value;
                          field.onChange(selectedFieldName);
                          if (
                            selectedFieldName ===
                              'sharedToUserInternalToDomain' ||
                            selectedFieldName === 'sharedToUserExternalToDomain'
                          ) {
                            setValue(
                              `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.operator`,
                              'equals'
                            );
                            setValue(
                              `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.value`,
                              'true'
                            );
                          } else {
                            setValue(
                              `conditionBlocks.${blockIndex}.conditions.${conditionIndex}.value`,
                              ''
                            );
                          }
                        }}
                      >
                        {calendarSharingFields.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.display}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
              </Grid>
              {showValueInput && (
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Controller
                    name={`conditionBlocks.${blockIndex}.conditions.${conditionIndex}.value`}
                    control={control}
                    render={({ field }) => {
                      if (condition.fieldName === 'domain') {
                        return (
                          <DomainAutocomplete
                            autoCompleteValue={field.value || ''}
                            setAutoCompleteValue={(val: string) =>
                              field.onChange(val)
                            }
                            domains={domains}
                            size="small"
                          />
                        );
                      }
                      if (condition.fieldName === 'sharedToGroups') {
                        return (
                          <GroupsAutocomplete
                            autocompleteValue={field.value || ''}
                            setAutoCompleteValue={(val) => field.onChange(val)}
                            label="Shared to"
                            freeSolo
                            size="small"
                          />
                        );
                      }
                      return (
                        <UsersAutocomplete
                          autocompleteValue={field.value || ''}
                          setAutoCompleteValue={(val) => field.onChange(val)}
                          label="Shared to"
                          freeSolo
                          size="small"
                        />
                      );
                    }}
                  />
                </Grid>
              )}
              <Grid
                size={{ xs: 12, sm: 12, md: 1 }}
                sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}
              >
                <Tooltip title="Delete condition">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const updatedConditions = conditions.filter(
                        (_: any, i: number) => i !== conditionIndex
                      );
                      if (updatedConditions.length === 0) {
                        setValue(
                          'conditionBlocks',
                          conditionBlocks.filter(
                            (_: any, index: number) => index !== blockIndex
                          )
                        );
                        return;
                      }
                      const updatedBlock = {
                        ...conditionBlocks[blockIndex],
                        conditions: updatedConditions,
                      };
                      const updatedBlocks = [...conditionBlocks];
                      updatedBlocks[blockIndex] = updatedBlock;
                      setValue('conditionBlocks', updatedBlocks);
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        );
      })}
    </Stack>
  );
}
